/**
 * Orchestrator Agent - System Prompt Builder
 * 
 * Assembles the orchestrator's system prompt from 5 standardized sections.
 * Each section can be fetched from Keywords AI or fall back to code.
 * 
 * ## Sections
 * 
 * 1. **Intro** - Who the orchestrator is and its goals
 * 2. **Tools** - Available tools with usage guidance (dynamically generated)
 * 3. **Suggestions** - Best practices and workflow tips
 * 4. **Context Note** - How context IDs and artifacts work
 * 5. **Evaluation** - Performance criteria and final reminders
 */

import { SystemPromptBuilder } from '../../base/systemPrompt';
import type { ToolBase } from '../../base/toolBase';
import { getSharedVariable } from '../../../prompts';

// Section getters and constants
import { getIntroSection, ORCHESTRATOR_INTRO } from './intro';
import { 
    buildToolsSection, 
    buildCategorizedToolsSection,
    getToolsPreamble,
    getToolsPostamble,
    ORCHESTRATOR_TOOLS_PREAMBLE,
    ORCHESTRATOR_TOOLS_POSTAMBLE,
    TOOL_CATEGORIES,
    CATEGORY_HEADERS,
    CATEGORY_ORDER,
    type ToolCategory,
} from './tools';
import { getSuggestionsSection, ORCHESTRATOR_SUGGESTIONS } from './suggestions';
import { getContextNoteSection, ORCHESTRATOR_CONTEXT_NOTE } from './contextNote';
import { getEvaluationSection, ORCHESTRATOR_EVALUATION } from './evaluation';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface OrchestratorPromptParams {
    /** Available tools (for generating the tools section) */
    tools: ToolBase[];
    /** Current deck ID (for context) */
    currentdocumentId?: string;
    /** Whether to use categorized tool format */
    categorizedTools?: boolean;
    /** Current Date and Timestampe */
    currentDateTime?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// PROMPT BUILDER
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Build the orchestrator's system prompt.
 * Uses Keywords AI prompts if available, otherwise falls back to code.
 */
export function buildOrchestratorSystemPrompt(params: OrchestratorPromptParams): string {
    const builder = new SystemPromptBuilder();
    const curDateTime = getSharedVariable('currentDateTime');

    const systemPrompt = builder.build({
        intro: ORCHESTRATOR_INTRO,
        tools: params.categorizedTools 
            ? buildCategorizedToolsSection(params.tools, true)
            : buildToolsSection(params.tools, true),
        suggestions: ORCHESTRATOR_SUGGESTIONS.replace('{{currentDateTime}}', curDateTime),
        contextNote: ORCHESTRATOR_CONTEXT_NOTE,
        evaluation: ORCHESTRATOR_EVALUATION.replace('{{securityWarnings}}', getSharedVariable('securityWarnings')),
    });
    return systemPrompt;
}

// ═══════════════════════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════════════════════

export {
    // Section builders (tools only - intro/suggestions/contextNote/evaluation use getters)
    buildToolsSection,
    buildCategorizedToolsSection,
    
    // Section getters (Keywords AI + fallback)
    getIntroSection,
    getToolsPreamble,
    getToolsPostamble,
    getSuggestionsSection,
    getContextNoteSection,
    getEvaluationSection,
    
    // Section constants (code fallback)
    ORCHESTRATOR_INTRO,
    ORCHESTRATOR_SUGGESTIONS,
    ORCHESTRATOR_CONTEXT_NOTE,
    ORCHESTRATOR_EVALUATION,
    ORCHESTRATOR_TOOLS_PREAMBLE,
    ORCHESTRATOR_TOOLS_POSTAMBLE,
    
    // Tool categorization
    TOOL_CATEGORIES,
    CATEGORY_HEADERS,
    CATEGORY_ORDER,
};

// Variables and prompt configuration
export {
    ORCHESTRATOR_VARIABLES,
    ORCHESTRATOR_PROMPT_KEYS,
    ORCHESTRATOR_PROMPT_CONFIG,
} from './variables';

export type { ToolCategory };

export type { ToolBase };
